import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { appConfig, HOME_ROUTE } from '@/config/app';
import { apiFetch } from '@/lib/api';
import { getCountryOptions } from '@/lib/countries';
import { t, setLocale } from '@/lib/i18n';
import { MIN_PASSWORD_LENGTH, PASSWORD_CHECKS, isPasswordStrong, validatePassword } from '@/lib/passwordPolicy';

type Step = 'account' | 'organization';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
];

/** Extract a slug from a URL domain, e.g. https://www.about-zero.de → about-zero */
function slugFromUrl(raw: string): string {
  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, '');
    // Take domain name without TLD: about-zero.de → about-zero
    const parts = host.split('.');
    // Handle single-part domains (localhost) or multi-part (about-zero.co.uk)
    const name = parts.length >= 2 ? parts.slice(0, -1).join('-') : parts[0];
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  } catch {
    return '';
  }
}

function isValidUrl(value: string): boolean {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidEmail(value: string): boolean {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('account');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Account fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // B30: inline email-existence error (shown on blur + handleNextStep)
  const [emailExistsError, setEmailExistsError] = useState<string | null>(null);

  // Org fields
  const [orgName, setOrgName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteUrlError, setWebsiteUrlError] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailError, setContactEmailError] = useState<string | null>(null);
  const [orgSlug, setOrgSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [isConsumerEmail, setIsConsumerEmail] = useState(false);
  const [addressStreet, setAddressStreet] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressZip, setAddressZip] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('en');

  // Slug suggestion from email
  useEffect(() => {
    if (step !== 'organization' || !email) return;
    (async () => {
      try {
        const res = await apiFetch(`/api/register/suggest?email=${encodeURIComponent(email)}`);
        const json = await res.json();
        if (json.success) {
          const { suggestedSlug, suggestedOrgName, isConsumerEmail: isConsumer } = json.data;
          setIsConsumerEmail(isConsumer);
          if (suggestedSlug && !orgSlug) setOrgSlug(suggestedSlug);
          if (suggestedOrgName && !orgName) setOrgName(suggestedOrgName);
        }
      } catch { /* ignore */ }
    })();
  }, [step, email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced slug availability check
  useEffect(() => {
    if (!orgSlug || orgSlug.length < 2) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/register/check-slug?slug=${encodeURIComponent(orgSlug)}`);
        const json = await res.json();
        if (json.success) setSlugAvailable(json.data.available);
      } catch { /* ignore */ } finally {
        setSlugChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [orgSlug]);

  // Try to detect browser language
  useEffect(() => {
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && LANGUAGES.some(l => l.value === browserLang)) {
      setDefaultLanguage(browserLang);
    }
  }, []);

  // B30: check email existence on blur (early feedback before Continue is clicked)
  const checkEmailExists = useCallback(async (emailValue: string) => {
    if (!emailValue.trim() || !isValidEmail(emailValue)) {
      setEmailExistsError(null);
      return false;
    }
    try {
      const res = await apiFetch(`/api/register/check-email?email=${encodeURIComponent(emailValue.trim().toLowerCase())}`);
      const json = await res.json();
      if (json.success && json.data.exists) {
        setEmailExistsError(t('An account with this email already exists.'));
        return true;
      } else {
        setEmailExistsError(null);
        return false;
      }
    } catch {
      setEmailExistsError(null);
      return false;
    }
  }, []);

  const handleNextStep = useCallback(async () => {
    setError(null);
    if (!fullName.trim()) return setError(t('Full name is required'));
    if (!email.trim()) return setError(t('Email is required'));
    if (!isPasswordStrong(password)) {
      const { failedChecks } = validatePassword(password);
      return setError(`${t('Password requirements not met')}: ${failedChecks.join(', ')}`);
    }
    if (password !== confirmPassword) return setError(t('Passwords do not match'));
    // B30: check if email is already taken before advancing to step 2
    const emailTaken = await checkEmailExists(email.trim().toLowerCase());
    if (emailTaken) return; // emailExistsError is already set
    setStep('organization');
  }, [fullName, email, password, confirmPassword, checkEmailExists]);

  const handleSubmit = useCallback(async () => {
    setError(null);
    if (!orgName.trim()) return setError(t('Organization name is required'));
    if (websiteUrl && !isValidUrl(websiteUrl)) return setError(t('Enter a valid URL starting with http:// or https://'));
    if (!orgSlug || orgSlug.length < 2) return setError(t('Organization URL is required (min 2 characters)'));
    if (slugAvailable === false) return setError(t('This organization URL is already taken'));
    if (contactEmail && !isValidEmail(contactEmail)) return setError(t('Enter a valid email address'));

    setIsSubmitting(true);
    try {
      const res = await apiFetch('/api/auth/register-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          password,
          orgName: orgName.trim(),
          orgSlug,
          websiteUrl: websiteUrl || undefined,
          contactEmail: contactEmail || undefined,
          addressStreet: addressStreet || undefined,
          addressCity: addressCity || undefined,
          addressZip: addressZip || undefined,
          addressCountry: addressCountry || undefined,
          defaultLanguage,
        }),
      });
      const json = await res.json();

      if (!res.ok || !json.success) {
        setError(json.error?.message || t('Registration failed'));
        return;
      }

      // Set the chosen language locale immediately so first page load uses it
      if (defaultLanguage && defaultLanguage !== 'en') {
        await setLocale(defaultLanguage);
      }

      // Auto-logged in via cookies — redirect to home route
      navigate(HOME_ROUTE, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Registration failed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [orgName, orgSlug, slugAvailable, fullName, email, password, websiteUrl, contactEmail, addressStreet, addressCity, addressZip, addressCountry, defaultLanguage, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/images/logo.png"
            alt={appConfig.name}
            className="h-12 object-contain"
          />
        </div>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Create your organization
        </p>

        {/* Step indicator */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === 'account' ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-700'
            }`}>1</div>
            <span className="text-sm font-medium text-foreground">Account</span>
          </div>
          <div className="w-8 h-px bg-border" />
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === 'organization' ? 'bg-primary-600 text-white' : 'bg-muted text-muted-foreground'
            }`}>2</div>
            <span className={`text-sm font-medium ${step === 'organization' ? 'text-foreground' : 'text-muted-foreground'}`}>Organization</span>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card py-8 px-6 shadow-sm rounded-lg sm:px-10 border border-border">
          {error && <Alert variant="error" className="mb-4">{error}</Alert>}

          {step === 'account' && (
            <div className="space-y-4">
              <Input
                label="Full Name"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="John Doe"
                required
                autoFocus
              />
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailExistsError(null); }}
                onBlur={e => checkEmailExists(e.target.value)}
                placeholder="john@acme.com"
                required
                error={emailExistsError || undefined}
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={`Min. ${MIN_PASSWORD_LENGTH} characters`}
                minLength={MIN_PASSWORD_LENGTH}
                required
              />

              {/* Password strength checklist — matches the backend strongPassword schema */}
              {password.length > 0 && (
                <div className="space-y-1.5">
                  {PASSWORD_CHECKS.map((check, i) => {
                    const passed = check.test(password);
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={passed ? 'text-success' : 'text-muted-foreground'}>
                          {passed ? '✓' : '○'}
                        </span>
                        <span className={passed ? 'text-success' : 'text-muted-foreground'}>
                          {check.label()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                minLength={MIN_PASSWORD_LENGTH}
                required
              />
              <Button onClick={handleNextStep} className="w-full">
                Continue
              </Button>
            </div>
          )}

          {step === 'organization' && (
            <div className="space-y-4">
              <Input
                label={t('Organization Name')}
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="Acme Corporation"
                required
                autoFocus
              />

              <Input
                label={t('Website URL')}
                type="url"
                value={websiteUrl}
                onChange={e => {
                  const v = e.target.value;
                  setWebsiteUrl(v);
                  setWebsiteUrlError(v && !isValidUrl(v) ? t('Enter a valid URL starting with http:// or https://') : null);
                  // Auto-derive slug from URL domain (only if user hasn't manually edited the slug)
                  if (!slugManuallyEdited && v) {
                    const derived = slugFromUrl(v);
                    if (derived) setOrgSlug(derived);
                  }
                }}
                onBlur={() => setWebsiteUrlError(websiteUrl && !isValidUrl(websiteUrl) ? t('Enter a valid URL starting with http:// or https://') : null)}
                placeholder="https://www.example.com"
                error={websiteUrlError || undefined}
              />

              <div>
                <Input
                  label={t('Organization URL')}
                  value={orgSlug}
                  onChange={e => {
                    setSlugManuallyEdited(true);
                    setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  }}
                  placeholder="acme-corp"
                  required
                />
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    Organization ID: {orgSlug || 'your-org'}
                  </span>
                  {slugChecking && (
                    <span className="text-xs text-muted-foreground">Checking...</span>
                  )}
                  {!slugChecking && slugAvailable === true && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Available
                    </span>
                  )}
                  {!slugChecking && slugAvailable === false && (
                    <span className="text-xs text-red-600">Already taken</span>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="pt-2">
                <h3 className="text-sm font-medium text-foreground mb-3">{t('Address')} <span className="text-muted-foreground font-normal">({t('optional')})</span></h3>
                <div className="space-y-3">
                  <Input
                    label={t('Contact Email')}
                    type="email"
                    value={contactEmail}
                    onChange={e => {
                      const v = e.target.value;
                      setContactEmail(v);
                      setContactEmailError(v && !isValidEmail(v) ? t('Enter a valid email address') : null);
                    }}
                    onBlur={() => setContactEmailError(contactEmail && !isValidEmail(contactEmail) ? t('Enter a valid email address') : null)}
                    placeholder="info@example.com"
                    error={contactEmailError || undefined}
                  />
                  <Input
                    label={t('Street')}
                    value={addressStreet}
                    onChange={e => setAddressStreet(e.target.value)}
                    placeholder="123 Main Street"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="ZIP / Postal Code"
                      value={addressZip}
                      onChange={e => setAddressZip(e.target.value)}
                      placeholder="10001"
                    />
                    <Input
                      label="City"
                      value={addressCity}
                      onChange={e => setAddressCity(e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Country</label>
                    <select
                      value={addressCountry}
                      onChange={e => setAddressCountry(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {getCountryOptions().map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Default Language</label>
                <select
                  value={defaultLanguage}
                  onChange={e => setDefaultLanguage(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {LANGUAGES.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setStep('account'); setError(null); }} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || slugAvailable === false || !!websiteUrlError || !!contactEmailError}
                  isLoading={isSubmitting}
                  className="flex-1"
                >
                  Create Organization
                </Button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/auth" className="text-primary-600 hover:text-primary-800 font-medium">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
